import type { RoofAggregates, RoofLineClass, RoofObjectType, RoofvizPacket, XY } from "./contract.ts";
import { solvePacket, type SolveResult } from "./solver.ts";

export const MAX_RENDER_CLOSURE_M = 0.05;

export type RenderSafetyCode =
  | "unsupported_packet"
  | "missing_data"
  | "duplicate_id"
  | "invalid_reference"
  | "non_finite"
  | "unsafe_geometry"
  | "solver_failure";

export interface RenderSafetyIssue {
  code: RenderSafetyCode;
  path: string;
  message: string;
}

export interface RenderSafetyWarning {
  code: "aggregate_reconciliation";
  path: string;
  message: string;
}

export type RenderSafetyResult =
  | { ok: true; packet: RoofvizPacket; solved: SolveResult; warnings: RenderSafetyWarning[] }
  | { ok: false; errors: RenderSafetyIssue[]; warnings: RenderSafetyWarning[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const ROOF_LINE_CLASSES: readonly RoofLineClass[] = [
  "ridge",
  "hip",
  "valley",
  "eave",
  "rake",
  "gutter",
  "step_flashing",
  "apron_flashing",
  "parapet",
];

const ROOF_OBJECT_TYPES: readonly RoofObjectType[] = [
  "skylight",
  "chimney",
  "vent",
  "solar_array",
  "antenna",
];

const pointKey = (point: XY): string => `${point[0].toFixed(4)},${point[1].toFixed(4)}`;

function pushFinite(
  value: unknown,
  path: string,
  errors: RenderSafetyIssue[],
  options: { positive?: boolean; integer?: boolean } = {},
): value is number {
  if (!isFiniteNumber(value)) {
    errors.push({ code: "non_finite", path, message: "Must be a finite number." });
    return false;
  }
  if (options.positive && value <= 0) {
    errors.push({ code: "unsafe_geometry", path, message: "Must be greater than zero." });
    return false;
  }
  if (options.integer && !Number.isInteger(value)) {
    errors.push({ code: "unsafe_geometry", path, message: "Must be a whole number." });
    return false;
  }
  return true;
}

function orientation(a: XY, b: XY, c: XY): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

function onSegment(a: XY, b: XY, p: XY): boolean {
  const epsilon = 1e-9;
  return (
    Math.abs(orientation(a, b, p)) <= epsilon &&
    p[0] >= Math.min(a[0], b[0]) - epsilon &&
    p[0] <= Math.max(a[0], b[0]) + epsilon &&
    p[1] >= Math.min(a[1], b[1]) - epsilon &&
    p[1] <= Math.max(a[1], b[1]) + epsilon
  );
}

function segmentsIntersect(a: XY, b: XY, c: XY, d: XY): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  const epsilon = 1e-9;
  if (Math.abs(abC) <= epsilon && onSegment(a, b, c)) return true;
  if (Math.abs(abD) <= epsilon && onSegment(a, b, d)) return true;
  if (Math.abs(cdA) <= epsilon && onSegment(c, d, a)) return true;
  if (Math.abs(cdB) <= epsilon && onSegment(c, d, b)) return true;
  return (abC > 0) !== (abD > 0) && (cdA > 0) !== (cdB > 0);
}

function validateRing(ring: unknown, path: string, errors: RenderSafetyIssue[]): ring is XY[] {
  if (!Array.isArray(ring) || ring.length < 3) {
    errors.push({ code: "unsafe_geometry", path, message: "A roof plane needs at least three vertices." });
    return false;
  }

  let valid = true;
  const points: XY[] = [];
  ring.forEach((value, index) => {
    if (!Array.isArray(value) || value.length !== 2) {
      errors.push({ code: "unsafe_geometry", path: `${path}[${index}]`, message: "Expected an [x, y] coordinate." });
      valid = false;
      return;
    }
    if (!isFiniteNumber(value[0]) || !isFiniteNumber(value[1])) {
      errors.push({ code: "non_finite", path: `${path}[${index}]`, message: "Coordinates must be finite." });
      valid = false;
      return;
    }
    points.push([value[0], value[1]]);
  });
  if (!valid) return false;

  const unique = new Set(points.map(pointKey));
  if (unique.size !== points.length) {
    errors.push({ code: "unsafe_geometry", path, message: "A roof plane cannot repeat a vertex." });
    valid = false;
  }

  const doubledArea = points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point[0] * next[1] - next[0] * point[1];
  }, 0);
  if (Math.abs(doubledArea) <= 1e-8) {
    errors.push({ code: "unsafe_geometry", path, message: "A roof plane cannot have zero area." });
    valid = false;
  }

  for (let i = 0; i < points.length; i += 1) {
    const iNext = (i + 1) % points.length;
    for (let j = i + 1; j < points.length; j += 1) {
      const jNext = (j + 1) % points.length;
      if (i === j || iNext === j || jNext === i) continue;
      if (segmentsIntersect(points[i], points[iNext], points[j], points[jNext])) {
        errors.push({ code: "unsafe_geometry", path, message: "A roof plane cannot self-intersect." });
        return false;
      }
    }
  }

  return valid;
}

function validateUniqueIds(
  values: unknown[],
  path: string,
  errors: RenderSafetyIssue[],
): Set<string> {
  const ids = new Set<string>();
  values.forEach((value, index) => {
    if (!isRecord(value) || typeof value.id !== "string" || value.id.trim() === "") {
      errors.push({ code: "missing_data", path: `${path}[${index}].id`, message: "A non-empty identifier is required." });
      return;
    }
    if (ids.has(value.id)) {
      errors.push({ code: "duplicate_id", path: `${path}[${index}].id`, message: `Duplicate identifier '${value.id}'.` });
    }
    ids.add(value.id);
  });
  return ids;
}

function validateAggregates(value: unknown, errors: RenderSafetyIssue[]): value is RoofAggregates {
  if (!isRecord(value)) {
    errors.push({ code: "missing_data", path: "aggregates", message: "Roof aggregates are required." });
    return false;
  }
  const keys: (keyof RoofAggregates)[] = [
    "planAreaM2",
    "surfaceAreaM2",
    "storeys",
    "ridgeM",
    "hipM",
    "valleyM",
    "eaveM",
  ];
  let valid = true;
  keys.forEach((key) => {
    const numberValid = pushFinite(value[key], `aggregates.${key}`, errors, {
      positive: key === "planAreaM2" || key === "surfaceAreaM2" || key === "storeys",
      integer: key === "storeys",
    });
    if (!numberValid) valid = false;
    else if ((value[key] as number) < 0) {
      errors.push({ code: "unsafe_geometry", path: `aggregates.${key}`, message: "Cannot be negative." });
      valid = false;
    }
  });
  return valid;
}

function compareAggregate(
  path: keyof Pick<RoofAggregates, "planAreaM2" | "surfaceAreaM2" | "ridgeM" | "hipM" | "valleyM" | "eaveM">,
  packetValue: number,
  solverValue: number,
  warnings: RenderSafetyWarning[],
): void {
  const difference = Math.abs(packetValue - solverValue);
  const tolerance = Math.max(1, Math.abs(packetValue) * 0.02);
  if (difference > tolerance) {
    warnings.push({
      code: "aggregate_reconciliation",
      path: `aggregates.${path}`,
      message: `Packet aggregate differs from the render solver by ${difference.toFixed(2)} m${path.includes("Area") ? "²" : ""}.`,
    });
  }
}

export function validatePacketForRendering(input: unknown): RenderSafetyResult {
  const errors: RenderSafetyIssue[] = [];
  const warnings: RenderSafetyWarning[] = [];

  if (!isRecord(input) || input.packet !== "roofviz_packet_v1" || input.units !== "meters") {
    return {
      ok: false,
      errors: [{ code: "unsupported_packet", path: "packet", message: "This roof data format is not supported." }],
      warnings,
    };
  }

  if (!isRecord(input.property)) {
    errors.push({ code: "missing_data", path: "property", message: "Property identity is required." });
  } else {
    if (typeof input.property.trackingId !== "string" || input.property.trackingId.trim() === "") {
      errors.push({ code: "missing_data", path: "property.trackingId", message: "A property tracking identifier is required." });
    }
    if (typeof input.property.label !== "string" || input.property.label.trim() === "") {
      errors.push({ code: "missing_data", path: "property.label", message: "A homeowner-safe property label is required." });
    }
    if (input.property.suburb !== undefined && typeof input.property.suburb !== "string") {
      errors.push({ code: "missing_data", path: "property.suburb", message: "Property suburb must be text when supplied." });
    }
  }
  if (!(input.crs === "local_xy_meters" || input.crs === "wgs84_local")) {
    errors.push({ code: "unsupported_packet", path: "crs", message: "This roof coordinate reference is not supported." });
  }

  const levels = Array.isArray(input.levels) ? input.levels : [];
  const planes = Array.isArray(input.planes) ? input.planes : [];
  const lines = Array.isArray(input.lines) ? input.lines : [];
  const objects = Array.isArray(input.objects) ? input.objects : [];
  if (levels.length === 0) errors.push({ code: "missing_data", path: "levels", message: "At least one roof level is required." });
  if (planes.length === 0) errors.push({ code: "missing_data", path: "planes", message: "At least one roof plane is required." });
  if (!Array.isArray(input.lines)) errors.push({ code: "missing_data", path: "lines", message: "Roof lines are required." });
  if (!Array.isArray(input.objects)) errors.push({ code: "missing_data", path: "objects", message: "Roof objects must be supplied as a list." });

  const levelIds = validateUniqueIds(levels, "levels", errors);
  const planeIds = validateUniqueIds(planes, "planes", errors);

  levels.forEach((level, index) => {
    if (!isRecord(level)) return;
    if (typeof level.name !== "string" || level.name.trim() === "") {
      errors.push({ code: "missing_data", path: `levels[${index}].name`, message: "A level name is required." });
    }
    pushFinite(level.eaveHeightM, `levels[${index}].eaveHeightM`, errors);
  });

  const knownVertexKeys = new Set<string>();
  planes.forEach((planeValue, index) => {
    if (!isRecord(planeValue)) {
      errors.push({ code: "missing_data", path: `planes[${index}]`, message: "A roof plane object is required." });
      return;
    }
    const path = `planes[${index}]`;
    if (typeof planeValue.levelId !== "string" || !levelIds.has(planeValue.levelId)) {
      errors.push({ code: "invalid_reference", path: `${path}.levelId`, message: "The roof level cannot be resolved." });
    }
    if (pushFinite(planeValue.pitchDeg, `${path}.pitchDeg`, errors, { positive: true }) && planeValue.pitchDeg >= 90) {
      errors.push({ code: "unsafe_geometry", path: `${path}.pitchDeg`, message: "Roof pitch must be below 90 degrees." });
    }
    if (!(["measured", "manual", "assumed"] as unknown[]).includes(planeValue.pitchSource)) {
      errors.push({ code: "missing_data", path: `${path}.pitchSource`, message: "Pitch provenance is required." });
    }
    const ring = planeValue.ring;
    const ringValid = validateRing(ring, `${path}.ring`, errors);
    if (ringValid) ring.forEach((point) => knownVertexKeys.add(pointKey(point)));
    if (!Array.isArray(planeValue.eaveEdge) || planeValue.eaveEdge.length !== 2) {
      errors.push({ code: "unsafe_geometry", path: `${path}.eaveEdge`, message: "An eave edge must reference two ring vertices." });
    } else if (ringValid) {
      const [first, second] = planeValue.eaveEdge;
      if (typeof first !== "number" || typeof second !== "number" || !Number.isInteger(first) || !Number.isInteger(second) || first === second || first < 0 || second < 0 || first >= ring.length || second >= ring.length) {
        errors.push({ code: "unsafe_geometry", path: `${path}.eaveEdge`, message: "The eave edge reference is invalid." });
      } else {
        const a = ring[first];
        const b = ring[second];
        if (Math.hypot(a[0] - b[0], a[1] - b[1]) <= 1e-8) {
          errors.push({ code: "unsafe_geometry", path: `${path}.eaveEdge`, message: "The eave edge must have length." });
        }
      }
    }
  });

  lines.forEach((lineValue, index) => {
    if (!isRecord(lineValue)) {
      errors.push({ code: "missing_data", path: `lines[${index}]`, message: "A roof line object is required." });
      return;
    }
    const path = `lines[${index}]`;
    if (!ROOF_LINE_CLASSES.includes(lineValue.class as RoofLineClass)) {
      errors.push({ code: "unsupported_packet", path: `${path}.class`, message: "The roof line class is not supported." });
    }
    if (!Array.isArray(lineValue.polyline) || lineValue.polyline.length < 2) {
      errors.push({ code: "unsafe_geometry", path: `${path}.polyline`, message: "A roof line needs at least two points." });
    } else {
      lineValue.polyline.forEach((point, pointIndex) => {
        if (!Array.isArray(point) || point.length !== 2 || !isFiniteNumber(point[0]) || !isFiniteNumber(point[1])) {
          errors.push({ code: "non_finite", path: `${path}.polyline[${pointIndex}]`, message: "Line coordinates must be finite." });
        }
      });
    }
    if (!Array.isArray(lineValue.planeIds) || lineValue.planeIds.length === 0) {
      errors.push({ code: "invalid_reference", path: `${path}.planeIds`, message: "A roof line must resolve to at least one plane." });
      return;
    }
    lineValue.planeIds.forEach((planeId, planeIndex) => {
      if (typeof planeId !== "string" || !planeIds.has(planeId)) {
        errors.push({ code: "invalid_reference", path: `${path}.planeIds[${planeIndex}]`, message: "The owning roof plane cannot be resolved." });
      }
    });
    if (Array.isArray(lineValue.polyline)) {
      lineValue.polyline.forEach((point, pointIndex) => {
        if (!Array.isArray(point) || point.length !== 2 || !isFiniteNumber(point[0]) || !isFiniteNumber(point[1])) return;
        if (!knownVertexKeys.has(pointKey([point[0], point[1]]))) {
          errors.push({ code: "invalid_reference", path: `${path}.polyline[${pointIndex}]`, message: "The line point cannot be resolved to the roof mesh." });
        }
      });
    }
  });

  objects.forEach((objectValue, index) => {
    if (!isRecord(objectValue)) {
      errors.push({ code: "missing_data", path: `objects[${index}]`, message: "A roof object is required." });
      return;
    }
    const path = `objects[${index}]`;
    if (!ROOF_OBJECT_TYPES.includes(objectValue.type as RoofObjectType)) {
      errors.push({ code: "unsupported_packet", path: `${path}.type`, message: "The roof object type is not supported." });
    }
    if (typeof objectValue.planeId !== "string" || !planeIds.has(objectValue.planeId)) {
      errors.push({ code: "invalid_reference", path: `${path}.planeId`, message: "The object's roof plane cannot be resolved." });
    }
    if (!Array.isArray(objectValue.at) || objectValue.at.length !== 2 || !isFiniteNumber(objectValue.at[0]) || !isFiniteNumber(objectValue.at[1])) {
      errors.push({ code: "non_finite", path: `${path}.at`, message: "Object coordinates must be finite." });
    }
    if (objectValue.sizeM !== undefined) {
      if (!Array.isArray(objectValue.sizeM) || objectValue.sizeM.length !== 2) {
        errors.push({ code: "unsafe_geometry", path: `${path}.sizeM`, message: "Object size must contain width and length." });
      } else {
        pushFinite(objectValue.sizeM[0], `${path}.sizeM[0]`, errors, { positive: true });
        pushFinite(objectValue.sizeM[1], `${path}.sizeM[1]`, errors, { positive: true });
      }
    }
  });

  validateAggregates(input.aggregates, errors);
  if (errors.length > 0) return { ok: false, errors, warnings };

  let solved: SolveResult;
  try {
    solved = solvePacket(input as unknown as RoofvizPacket);
  } catch {
    return {
      ok: false,
      errors: [{ code: "solver_failure", path: "planes", message: "The roof could not be prepared for display." }],
      warnings,
    };
  }

  const unsafeSolverValue =
    !Number.isFinite(solved.maxClosureDevM) ||
    solved.planes.some((plane) =>
      !Number.isFinite(plane.planAreaM2) ||
      !Number.isFinite(plane.trueAreaM2) ||
      plane.planAreaM2 <= 0 ||
      plane.trueAreaM2 <= 0 ||
      plane.verts.some((vertex) => !Number.isFinite(vertex.x) || !Number.isFinite(vertex.y) || !Number.isFinite(vertex.z)),
    ) ||
    solved.lines.some((line) => !Number.isFinite(line.lengthM) || line.lengthM <= 0 || line.pts3.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)));

  if (unsafeSolverValue) {
    return {
      ok: false,
      errors: [{ code: "unsafe_geometry", path: "solver", message: "The prepared roof contains unsafe geometry." }],
      warnings,
    };
  }
  if (solved.maxClosureDevM > MAX_RENDER_CLOSURE_M) {
    return {
      ok: false,
      errors: [{ code: "unsafe_geometry", path: "solver.closure", message: "Connected roof surfaces do not close safely." }],
      warnings,
    };
  }

  const packet = input as unknown as RoofvizPacket;
  compareAggregate("planAreaM2", packet.aggregates.planAreaM2, solved.totals.planAreaM2, warnings);
  compareAggregate("surfaceAreaM2", packet.aggregates.surfaceAreaM2, solved.totals.surfaceAreaM2, warnings);
  compareAggregate("ridgeM", packet.aggregates.ridgeM, solved.totals.ridgeM, warnings);
  compareAggregate("hipM", packet.aggregates.hipM, solved.totals.hipM, warnings);
  compareAggregate("valleyM", packet.aggregates.valleyM, solved.totals.valleyM, warnings);
  compareAggregate("eaveM", packet.aggregates.eaveM, solved.totals.eaveM, warnings);
  return { ok: true, packet, solved, warnings };
}

export function fallbackForValidation(result: Extract<RenderSafetyResult, { ok: false }>): {
  title: string;
  message: string;
} {
  if (result.errors.some((error) => error.code === "unsupported_packet")) {
    return { title: "This roof is not ready to view", message: "The supplied roof data uses an unsupported format." };
  }
  if (result.errors.some((error) => error.code === "unsafe_geometry" || error.code === "non_finite")) {
    return { title: "We cannot display this roof safely", message: "The roof data needs a geometry check before it can be visualised." };
  }
  return { title: "This roof needs more information", message: "Some required roof details are missing or cannot be resolved." };
}
